import { computed, reactive } from "vue";
import { useDrawerStore } from "@/stores/drawer";

declare type DrawerName = "campaign-editor" | "page-comment" | "page-editor" | "form-editor" | "submission-viewer";

interface DrawerOptions {
  autoClose?: boolean;
  overlay?: boolean;
  shadow?: boolean;
  selectedTab?: number;

  [key: string]: any;
}

const defaultOptions: DrawerOptions = {
  autoClose: true,
  overlay: true,
  shadow: true,
  selectedTab: 0,
};

export default function useDrawer(name: DrawerName, customOptions: DrawerOptions = {}) {
  const drawer = useDrawerStore();

  const mergedOptions = computed(() => ({
    ...defaultOptions,
    ...customOptions,
    ...drawer.options,
  }));

  const isOpen = computed(() => drawer.currentDrawer === name);
  const selectedTab = computed(() => mergedOptions.value.selectedTab);

  const show = (options: DrawerOptions = {}) => {
    drawer.setActiveDrawer(name, { ...mergedOptions.value, ...options });
  };

  const hide = () => {
    if (isOpen.value) {
      drawer.setActiveDrawer(null);
    }
  };

  const showTab = (tabIndex: number, options: DrawerOptions = {}) => {
    show({ ...options, selectedTab: tabIndex });
  };

  const toggle = (options: DrawerOptions = {}) => {
    if (isOpen.value) {
      hide();
    } else {
      show(options);
    }
  };

  const toggleTab = (tabIndex: number, options: DrawerOptions = {}) => {
    if (isOpen.value) {
      hide();
    } else {
      showTab(tabIndex, options);
    }
  };

  return reactive({
    isOpen,
    options: mergedOptions,
    selectedTab,
    toggle,
    toggleTab,
    show,
    showTab,
    hide,
  });
}
